/**
 * integraciones.ts — el catálogo de lo que Books PODRÍA recibir.
 *
 * ⚠️ Ninguna está conectada, y eso no es un dato de muestra pendiente de
 * rellenar: es el estado real del producto y un requisito explícito.
 *
 *   > «debe funcionar inicialmente sin integraciones, pero diseñado para que
 *   > las pueda tener de manera sencilla»
 *
 * El mockup de la Fase 0 mostraba Skoped Kids conectado con 42 asientos
 * procesados y una llave `sk_live_4f8c21ab90de77c3`. Convertirlo tal cual
 * habría producido una pantalla que afirma tres cosas falsas —que la
 * integración existe, que procesó asientos, y que hay una credencial viva— y
 * la tercera es la peor: una cadena con forma de llave real en pantalla
 * termina copiada a algún lado por alguien que cree que sirve.
 *
 * Lo que sí es real y sí vale la pena enseñar es el MAPEO: a qué cuentas del
 * catálogo iría cada evento. Eso no se inventa, sale del catálogo contable, y
 * es exactamente la pregunta que hace quien evalúa el producto.
 */

export type EstadoDeIntegracion =
  /** Existe el conector, falta conectarla. */
  | 'disponible'
  /** El conector todavía no está construido. */
  | 'planeada';

/**
 * Cómo entraría el dato. No es un detalle de presentación: una importación de
 * archivo NO tiene llave de API, y mostrarle una a alguien que va a subir un
 * CSV le hace buscar algo que no existe.
 */
export type Mecanismo =
  /** El sistema de origen empuja eventos y necesita credencial. */
  | 'api'
  /** La persona sube un archivo. Sin credencial. */
  | 'archivo'
  /** Books consulta a otro servicio de la suite con su token de servicio. */
  | 'servicio';

/**
 * ⚠️ TODO lo de este archivo ENTRA a Books. Ninguna de estas tarjetas describe
 * algo que Books emita.
 *
 * Parece obvio y no lo era: la tarjeta de facturación decía «comprobantes
 * emitidos y aceptados por Hacienda» sin nombrar QUIÉN emite, y ahí se
 * mezclaban dos canales distintos.
 *
 *   · Un inquilino factura a sus clientes → el emisor es el inquilino, y
 *     **Books lleva sus consecutivos**. El comprobante entra como asiento.
 *
 *   · Skoped Labs factura a los suyos → **el emisor es el hub**, no Books.
 *     Para los libros de SkopedLabs esas facturas son un hecho que ENTRA.
 *     Contrato del hub v0.5 §1 y respuesta 02 §7.
 *
 * El segundo caso no está construido ni tiene fecha. Se modela como entrada
 * para que el día que llegue no se construya al revés.
 */
export interface Integracion {
  readonly clave: string;
  /** Dos o tres letras para la marca de color. */
  readonly marca: string;
  /** Color del producto de origen, del tablero de familia de la marca. */
  readonly color: string;
  readonly nombre: string;
  /** `null` cuando es un nombre propio y no se traduce. */
  readonly nombreEn: string | null;
  readonly mecanismo: Mecanismo;
  readonly descripcion: string;
  readonly descripcionEn: string;
  readonly estado: EstadoDeIntegracion;
  /**
   * La etapa del plan (docs/00 §10) en que entra, o `'—'` si está fuera del MVP.
   *
   * Inventarle un número a algo que no está en el plan es prometer un
   * calendario. Solo la del hub tiene etapa: es la multi-moneda, que sí está.
   */
  readonly etapa: string;
  /** Moneda en que llegarían los eventos. */
  readonly moneda: string;
  /** A qué cuentas del catálogo iría el asiento. Sale del catálogo, no se inventa. */
  readonly mapeo: { readonly debe: string; readonly haber: string } | null;
  /**
   * Código de la única empresa a la que aplica, si aplica a una sola.
   *
   * No todas las integraciones sirven a todos los inquilinos: las facturas que
   * el hub emite por Skoped Labs solo le importan a los libros de SkopedLabs.
   * Mostrárselas a otro cliente sería ofrecerle algo que no puede tener.
   */
  readonly soloPara?: string;
}

export const INTEGRACIONES: readonly Integracion[] = [
  {
    clave: 'kids', marca: 'SK', color: '#E24C8B',
    nombre: 'Skoped Kids', nombreEn: null, mecanismo: 'api',
    descripcion: 'Cada cobro de suscripción crearía el asiento de ingreso, con su cliente y su flujo.',
    descripcionEn: 'Every subscription charge would create the revenue entry, with its customer and stream.',
    estado: 'planeada', etapa: '—', moneda: 'CRC',
    // ▶ Cuentas del catálogo de verdad. Decía `4102-01`, que NO EXISTE: la
    // 41xx del catálogo va 4110/4120/4130/4140 por flujo de ingreso, y no hay
    // ninguna 4101 ni 4102. El caso que lo vigilaba comparaba contra la lista
    // de `datos/muestra.ts` —escrita a mano y con esos códigos dentro—, así
    // que pasaba sin haber mirado nunca el catálogo.
    //
    // Kids vende suscripciones y el catálogo marca la 4120 con
    // `stream: subscription`: la cuenta sale de ahí y no de una elección.
    mapeo: { debe: '1110', haber: '4120' },
  },
  {
    clave: 'facturacion', marca: 'FE', color: '#1179C7',
    nombre: 'Facturación de su empresa', nombreEn: 'Your own invoicing', mecanismo: 'api',
    descripcion: 'Los comprobantes que USTED emite a sus clientes, con su consecutivo y su clave. El emisor es su empresa y Books lleva sus consecutivos.',
    descripcionEn: 'The vouchers YOU issue to your customers, with their sequence and key. Your company is the issuer and Books keeps its sequences.',
    estado: 'planeada', etapa: '—', moneda: 'CRC',
    // ▶ SIN MAPEO, y eso es la respuesta y no un hueco.
    //
    // Decía `haber: '4101-01'`, una cuenta que no existe. Y aunque existiera,
    // **una factura no tiene una sola cuenta de ingreso**: el catálogo separa
    // el ingreso por flujo —4110 mantenimiento, 4120 suscripciones, 4130
    // hosting, 4140 transaccional— porque de esa separación salen los índices
    // operativos. La cuenta la decide QUÉ se vendió en cada línea.
    //
    // Prometer una sola sería inventar el mapeo que esta pantalla dice que no
    // se inventa.
    mapeo: null,
  },
  {
    clave: 'hub_facturas', marca: 'SL', color: '#E0B23A',
    nombre: 'Facturas de Skoped Labs', nombreEn: 'Skoped Labs invoices', mecanismo: 'servicio',
    descripcion: 'Las facturas que el hub emite por Skoped Labs entran acá como un hecho. El emisor es el hub, no Books.',
    descripcionEn: 'The invoices the hub issues on behalf of Skoped Labs arrive here as a fact. The issuer is the hub, not Books.',
    estado: 'planeada', etapa: '—', moneda: 'CRC',
    // Lo mismo que arriba: la cuenta de ingreso depende de la línea.
    mapeo: null,
    soloPara: 'skopedlabs',
  },
  {
    clave: 'banco', marca: 'CSV', color: '#5B6B82',
    nombre: 'Conciliación bancaria', nombreEn: 'Bank reconciliation', mecanismo: 'archivo',
    descripcion: 'Importar el estado de cuenta y cuadrarlo contra los asientos. Colones y dólares.',
    descripcionEn: 'Import the bank statement and reconcile it against the entries. Colones and dollars.',
    estado: 'planeada', etapa: '—', moneda: 'CRC',
    mapeo: null,
  },
  {
    clave: 'hub', marca: 'HUB', color: '#0A5E9B',
    nombre: 'Skoped Hub', nombreEn: null, mecanismo: 'servicio',
    descripcion: 'El tipo de cambio del BCCR y los datos de identidad de los contribuyentes.',
    descripcionEn: 'The BCCR exchange rate and taxpayer identity data.',
    estado: 'disponible', etapa: 'E2', moneda: 'CRC',
    mapeo: null,
  },
];

/**
 * La bitácora de eventos. Vacía, porque no ha entrado ninguno.
 *
 * El tipo existe ya con su forma final para que la pantalla del día uno sea la
 * misma que la del día en que empiecen a llegar.
 */
export interface EventoDeIntegracion {
  readonly id: string;
  readonly cuando: string;
  readonly integracion: string;
  readonly descripcion: string;
  readonly resultado: 'procesado' | 'con_error' | 'en_espera';
  readonly folio: string | null;
}

export const EVENTOS: readonly EventoDeIntegracion[] = [];
